import { OPERATION_TYPE } from '../../operations/models/operations.models';
import { WorkflowStep } from '../models/workflows.models';

/**
 * Anclas de scroll multi-idioma del editor (NIF-530).
 *
 * Abrir cuando: errores de locale text / tabs de idioma / focus al invalidar stepper.
 * Puro: sin DOM ni FormGroup.
 */

export type WorkflowLocaleScrollAnchor = 'general-delayed' | 'home' | 'documents' | 'finish';

/** Ids DOM de anclas (coinciden con el HTML del shell). */
export const WORKFLOW_LOCALE_SCROLL_ANCHOR_IDS: Record<WorkflowLocaleScrollAnchor, string> = {
	'general-delayed': 'workflow-locale-anchor-delayed',
	home: 'workflow-locale-anchor-home',
	documents: 'workflow-locale-anchor-documents',
	finish: 'workflow-locale-anchor-finish',
};

/**
 * Path relativo del FormGroup multi-locale por operación (HOME description, DOC title, FINISH loading).
 *
 * @param operationType - `OPERATION_TYPE.*`
 * @returns Path bajo `operations.{type}.…` o `null`
 */
export function localeTextControlPathForOperation(operationType: string): string | null {
	switch (operationType) {
		case OPERATION_TYPE.HOME:
			return 'description';
		case OPERATION_TYPE.DOCUMENTS:
			return 'title';
		case OPERATION_TYPE.FINISH:
			return 'loading_text';
		default:
			return null;
	}
}

/**
 * Operación → ancla de scroll de locale.
 *
 * @param operationType - `OPERATION_TYPE.*`
 */
export function operationTypeToLocaleScrollAnchor(
	operationType: string,
): WorkflowLocaleScrollAnchor | null {
	switch (operationType) {
		case OPERATION_TYPE.HOME:
			return 'home';
		case OPERATION_TYPE.DOCUMENTS:
			return 'documents';
		case OPERATION_TYPE.FINISH:
			return 'finish';
		default:
			return null;
	}
}

/**
 * Infiera ancla de scroll a partir del path del primer control inválido.
 *
 * @param stepId - `WorkflowStep.GENERAL` | `OPERATIONS`
 * @param path - Path relativo del control inválido
 */
export function inferLocaleScrollAnchorFromPath(
	stepId: string,
	path: string[],
): WorkflowLocaleScrollAnchor | null {
	if (stepId === WorkflowStep.GENERAL && path.includes('delayed_process_warning_text')) {
		return 'general-delayed';
	}
	if (stepId === WorkflowStep.OPERATIONS) {
		if (path.includes('description')) {
			return 'home';
		}
		if (path.includes('title')) {
			return 'documents';
		}
		if (
			path.includes('loading_text') ||
			path.includes('ok_title') ||
			path.includes('ko_title') ||
			path.includes('cancelled_title') ||
			path.includes('pending_title')
		) {
			return 'finish';
		}
	}
	return null;
}

/**
 * Marca la pestaña de idioma seleccionada (sin mutar el array original).
 *
 * @param tabs - Tabs actuales
 * @param localeId - Id de idioma a seleccionar
 */
export function withSelectedWorkflowLocaleTab<T extends { id: string | number; selected?: boolean }>(
	tabs: readonly T[],
	localeId: string,
): T[] {
	const id = String(localeId);
	return tabs.map((t) => ({
		...t,
		selected: String(t.id) === id,
	}));
}
